/* eslint-disable no-nested-ternary */
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import PushPinIcon from '@mui/icons-material/PushPin';
import Visibility from '@mui/icons-material/Visibility';
import Star from '@mui/icons-material/Star';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  Chip,
  Tooltip,
} from '@mui/material';
import { patchEvent } from '@pingum/app-state-management/event/actions';
import {
  Delete,
  UploadFile,
} from '@mui/icons-material';
import {
  addHorseNote,
  updateHorseNote,
  addHorseFile,
  getHorseNoteHighlights,
  deleteHorseNote,
} from '../../../../../../../redux/horse/actions';
import NoteTypeSelect from '../../../../../../../components/EnumSelects/NoteTypeSelect';
import ImportanceSelect from '../../../../../../../components/EnumSelects/ImportanceSelect';
import Horse from '../../../../../../../components/Horse';
import ReactFileUpload from '../../../../../../../components/ReactFileUpload';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 500,
  bgcolor: 'background.paper',
  p: 2,
  overflow: 'auto',
  maxHeight: '95%',
};

const AddEditModal = ({
  open,
  defaultHighlight,
  onClose,
  horseId,
  pinnedHorseNoteId,
  horseNote,
  presetType,
  taskId,
  task,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();

  // React state
  const [pinToHorse, setPinToHorse] = React.useState(false);
  const [highlightOrder, setHighlightOrder] = React.useState(null);
  const [type, setType] = React.useState('Note');
  const [importance, setImportance] = React.useState('Inform');
  const [note, setNote] = React.useState('');
  const [noteValidation, setNoteValidation] = React.useState(null);
  const [title, setTitle] = React.useState('');
  const [titleValidation, setTitleValidation] = React.useState(null);
  const [noteDate, setNoteDate] = React.useState(null);
  const [files, setFiles] = React.useState([]);
  const [clientAccess, setClientAccess] = React.useState(false);
  const [information, setInformation] = React.useState({});
  const [savingError, setSavingError] = React.useState(null);
  const [filesToRemove, setFilesToRemove] = React.useState([]);
  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Redux state
  const isAddingHorseNotes = useSelector((state) => state.horse.isAddingHorseNotes);
  const isUpdatingHorseNotes = useSelector((state) => state.horse.isUpdatingHorseNotes);
  const isAddingHorseFile = useSelector((state) => state.horse.isAddingHorseFile);
  const horseNoteHighlights = useSelector((state) => state.horse.horseNoteHighlights);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const eventData = useSelector((state) => state.eventData.eventData);
  const isLoading = isAddingHorseNotes || isUpdatingHorseNotes || isAddingHorseFile;

  React.useEffect(() => {
    if (open) {
      // load horse highlights so we can know next order if neededs
      dispatch(getHorseNoteHighlights(horseId));
    }
    setConfirmDelete(false);
    setFiles([]);
    setFilesToRemove([]);
    setPinToHorse((pinnedHorseNoteId && horseNote)
      ? pinnedHorseNoteId === horseNote.id
      : false);
    setHighlightOrder((horseNote)
      ? horseNote.highlightOrder
      : (defaultHighlight && 100) || null);
    if (!horseNote) {
      setType(presetType || 'Note');
    } else {
      setType(horseNote.type);
    }
    setNote(horseNote
      ? horseNote.note
      : '');
    setNoteValidation(null);
    setTitle(horseNote
      ? horseNote.title
      : '');
    setTitleValidation(null);
    setImportance(horseNote
      ? horseNote.importance
      : 'Inform');
    setClientAccess(horseNote
      ? horseNote.clientAccess
      : false);
    setInformation(horseNote
      ? horseNote.information
      : {});
    setNoteDate(horseNote
      ? new Date(horseNote.createdTimestamp)
      : null)
  }, [horseNote, open]);

  const getEDNameFromId = (id) => (eventData.find((ed) => ed.id === id) || { name: 'Unknown' }).name;

  const validateFields = () => {
    if (!title || title.length < 2 || title.length > 150) {
      setTitleValidation('Please provide a title between 2 and 150 characters in length.');
      return false;
    } setTitleValidation(null);
    if (note.length > 2048) {
      setNoteValidation('Note cannot exceed 2048 characters in length..');
      return false;
    } setNoteValidation(null);
    return true;
  };

  const handleUpdateTask = (newDescription, newImportance, newType, removeNote = false) => {
    if (task && task.id) {
      const newEventData = task.eventData;
      if (removeNote) {
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)];
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)];
        delete newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)];
      } else {
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteEventDataId)] = {
          type: 'Text',
          value: `${newDescription}`,
        };
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteImportanceEventDataId)] = {
          type: 'Text',
          value: `${newImportance}`,
        };
        newEventData[getEDNameFromId(selectedBarn.barnMetadata.horseNoteTypeEventDataId)] = {
          type: 'Text',
          value: `${newType}`,
        };
      }
      dispatch(patchEvent(task.id, { eventData: newEventData }));
      onClose(true);
    } else {
      onClose(false);
    }
  };

  const addFilesAndPatchNote = (newHorseNote) => {
    const allRequests = [];
    for (let i = 0; i < files.length; i += 1) {
      allRequests.push(dispatch(addHorseFile(horseId, newHorseNote.id, files[i], clientAccess)));
    }

    // once all done, process
    Promise.all(allRequests).then((values) => {
      // eslint-disable-next-line no-console

      dispatch(updateHorseNote(
        horseId,
        newHorseNote.id,
        { information: { files: [...((newHorseNote.information && newHorseNote.information.files) || []), ...values] } },
      )).then(() => {
        handleUpdateTask(note, importance, type, false);
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse note. Please contact Questri support.');
        console.error(error);
      });
    });
  };

  const handleDelete = () => {
    dispatch(deleteHorseNote(horseId, horseNote.id)).then(() => {
      setConfirmDelete(false);
      handleUpdateTask(null, null, null, true);
      onClose();
    }).catch(() => {
      setConfirmDelete(false);
      setSavingError('There was an error deleting this note. Please contact support');
    });
  };

  const handleSave = () => {
    const isNewHorseNote = !horseNote;
    if (!validateFields()) { return; }

    const request = {
      setAsHorseActiveNote: pinToHorse,
      type,
      importance,
      note,
      title,
      clientAccess,
      information,
      noteDate,
      taskId,
      // If editing horse note and it has order, keep that order
      horseHighlight: (highlightOrder && !isNewHorseNote)
        ? horseNote.highlightOrder
        : highlightOrder
          ? horseNoteHighlights.length > 0
            ? (Math.max(...horseNoteHighlights.map((o) => o.highlightOrder)) + 1)
            : 1
          : null,
    };

    // Adding new location
    if (isNewHorseNote) {
      dispatch(addHorseNote(horseId, request)).then((newHorseNote) => {
        if (files.length > 0) {
          // upload files then patch note with files
          addFilesAndPatchNote(newHorseNote);
        } else {
          handleUpdateTask(note, importance, type, false);
        }
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to add this note to the horse. Please contact Questri support.');
        console.error(error);
      });
    } else {
      if (filesToRemove && filesToRemove.length > 0 && information.files && information.files.length > 0) {
        // remove any removed files
        const updatedFilesList = information.files.filter((f) => filesToRemove.indexOf(f) < 0);
        request.information = {
          ...request.information,
          files: updatedFilesList,
        };
      }
      dispatch(updateHorseNote(horseId, horseNote.id, {
        ...request,
        removeHorseHighlightOrder: !highlightOrder,
      })).then((updatedHorseNote) => {
        if (files.length > 0) {
          // upload files then patch note with files
          addFilesAndPatchNote(updatedHorseNote);
        } else {
          handleUpdateTask(note, importance, type, false);
        }
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update the horse note. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-add-horse-note"
      aria-describedby="modal-add-horse-note-description"
    >
      <Box sx={modalStyle}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              id="modal-add-horse-note-title"
              variant="h6"
              component="h2"
              align="center"
              gutterBottom
            >
              {horseNote
                ? 'Edit'
                : 'Add'} Horse Note {horseNote && `(${horseNote.type})`}
            </Typography>
          </Grid>

          {
            horseId && barnHorsesMap[horseId] && <Grid item xs={12} justifyContent="center" sx={{ display: 'flex' }}><Horse horse={barnHorsesMap[horseId][0].horse} size={48} /></Grid>
          }

          <Grid item xs={12} md={6} lg={8} xl={9}>
            <TextField
              value={title}
              onChange={(e) => { setTitle(e.target.value); }}
              fullWidth
              disabled={isLoading}
              label="Headline"
              error={!!titleValidation}
              helperText={titleValidation}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={4} xl={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Date"
                value={noteDate || new Date()}
                disabled={isLoading}
                onChange={(newValue) => {
                  setNoteDate(newValue.toDate());
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={6}>
            <NoteTypeSelect value={type} onChange={(e) => { setType(e.target.value); }} />
          </Grid>
          <Grid item xs={6}>
            <ImportanceSelect value={importance} onChange={(e) => { setImportance(e.target.value); }} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={note}
              onChange={(e) => { setNote(e.target.value); }}
              fullWidth
              disabled={isLoading}
              label="Note Details"
              multiline
              rows={3}
              error={!!noteValidation}
              helperText={noteValidation}
            />
          </Grid>
          <Grid item xs={12}>
            <ReactFileUpload
              value={files}
              onChange={setFiles}
              title="Add files to this note"
              disabled={isLoading}
            />
          </Grid>
          {
            horseNote && horseNote.information && horseNote.information.files
            && horseNote.information.files.filter((file) => filesToRemove.indexOf(file) < 0).length > 0
            && (
              <Grid item xs={12}>
                <FormControlLabel
                  labelPlacement="start"
                  control={(
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        ml: 2,
                      }}
                    >
                      {
                        horseNote && horseNote.information && horseNote.information.files && horseNote.information.files
                          .filter((file) => filesToRemove.indexOf(file) < 0).map((file) => (
                            <Chip key={`delete-file-${file}`} label={file} onDelete={() => { setFilesToRemove([...filesToRemove, file]) }} icon={<UploadFile />} variant="outlined" sx={{ maxWidth: 200 }} deleteIcon={<Delete />} />
                          ))
                      }
                    </Box>
                  )}
                  label="Current Attachments"
                />
              </Grid>
            )
          }
          <Grid item>
            <FormGroup>
              <Tooltip title="If checked, clients associated with this horse will be able to view the note.">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={clientAccess}
                      onChange={(e) => { setClientAccess(e.target.checked); }}
                      disabled={isLoading}
                    />
                  )}
                  label={(
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{
                          mr: 0.5,
                          color: 'rgba(0,0,0,0.87)',
                        }}
                      >Share with client
                      </Typography>
                      <Visibility fontSize="medium" color="primary" />
                    </Box>
                  )}
                />
              </Tooltip>
            </FormGroup>
          </Grid>
          <Grid item>
            <FormGroup>
              <Tooltip title="If pinned, this status will be shown when users view the horse. Any previous pin will be replaced with this new one.">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={pinToHorse}
                      onChange={(e) => { setPinToHorse(e.target.checked); }}
                      disabled={isLoading}
                    />
                  )}
                  label={(
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{
                          mr: 0.5,
                          color: 'rgba(0,0,0,0.87)',
                        }}
                      >Pin to Horse
                      </Typography>
                      <PushPinIcon fontSize="medium" color="primary" />
                    </Box>
                  )}
                />
              </Tooltip>
            </FormGroup>
          </Grid>
          <Grid item>
            <FormGroup>
              <Tooltip title="If checked, this note will be highlighted on the horses profile.">
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={!!highlightOrder}
                      onChange={(e) => {
                        setHighlightOrder(e.target.checked
                          ? 100
                          : null);
                      }}
                      disabled={isLoading}
                    />
                  )}
                  label={(
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{
                          mr: 0.5,
                          color: 'rgba(0,0,0,0.87)',
                        }}
                      >Horse Highlight
                      </Typography>
                      <Star fontSize="medium" color="primary" />
                    </Box>
                  )}
                />
              </Tooltip>
            </FormGroup>
          </Grid>
        </Grid>

        <Divider sx={{ mb: '1rem' }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {
            horseNote
              ? (
                <LoadingButton
                  onClick={() => {
                    if (confirmDelete) {
                      handleDelete();
                    } else {
                      setConfirmDelete(true);
                    }
                  }}
                  disabled={isLoading}
                  variant="outlined"
                  color="error"
                  loading={isLoading}
                >{confirmDelete
                  ? 'Confirm Delete'
                  : 'Delete'}
                </LoadingButton>
              )
              : <Box />
          }
          <Box>
            {savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
              <LoadingButton onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  defaultHighlight: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string.isRequired,
  pinnedHorseNoteId: PropTypes.string,
  horseNote: PropTypes.object,
  presetType: PropTypes.string,
  taskId: PropTypes.number,
  task: PropTypes.object,
};

AddEditModal.defaultProps = {
  defaultHighlight: false,
  horseNote: null,
  pinnedHorseNoteId: null,
  presetType: null,
  taskId: null,
  task: null,
};

export default AddEditModal;
